import React, { useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LockChip } from '../../../music24/components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import type { ReportFrequency } from './useNotificationPreferences';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import styles from './notifications.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = defineMessages({
  title: { id: 'app.sml.settings.notifications.digest.title', defaultMessage: 'Digest' },
  daily: { id: 'app.sml.settings.notifications.option.daily.label', defaultMessage: 'Daily' },
  dailyDescription: {
    id: 'app.sml.settings.notifications.option.daily.description',
    defaultMessage: "A summary every morning, covering yesterday's data.",
  },
  weekly: { id: 'app.sml.settings.notifications.option.weekly.label', defaultMessage: 'Weekly' },
  weeklyDescription: {
    id: 'app.sml.settings.notifications.option.weekly.description',
    defaultMessage: 'A bigger digest every Monday, covering the past week.',
  },
  off: { id: 'app.sml.settings.notifications.option.off.label', defaultMessage: 'Off' },
  offDescription: { id: 'app.sml.settings.notifications.option.off.description', defaultMessage: 'No email reports.' },
});

const OPTIONS: { value: ReportFrequency; label: keyof typeof messages; description: keyof typeof messages }[] = [
  { value: 'daily', label: 'daily', description: 'dailyDescription' },
  { value: 'weekly', label: 'weekly', description: 'weeklyDescription' },
  { value: 'off', label: 'off', description: 'offDescription' },
];

interface Props {
  value: ReportFrequency;
  /** The plan has no daily digest (Free): Daily shows the lock chip and opens the upgrade prompt. */
  dailyLocked: boolean;
  onChange: (value: ReportFrequency) => void;
  onLockedClick: () => void;
}

/** Daily, Weekly, Off as three radio tiles (design settings-redesign c1, c2). */
const DigestTilesComponent: React.FC<Props> = ({ value, dailyLocked, onChange, onLockedClick }) => {
  const intl = useIntl();
  const groupId = useId();
  return (
    <fieldset className={styles.tiles}>
      <legend className="visually-hidden">{intl.formatMessage(messages.title)}</legend>
      {OPTIONS.map((option) => {
        const isLocked = option.value === 'daily' && dailyLocked;
        const id = `${groupId}-${option.value}`;
        return (
          <label key={option.value} htmlFor={id} className={`${styles.tile}${isLocked ? ` ${styles.tileLocked}` : ''}`}>
            <span className={styles.tileTitle}>
              <input
                id={id}
                type="radio"
                name="reportFrequency"
                className="form-check-input mt-0"
                value={option.value}
                checked={value === option.value}
                onChange={() => (isLocked ? onLockedClick() : onChange(option.value))}
              />
              {intl.formatMessage(messages[option.label])}
              {isLocked && <LockChip feature={SMLUpgradeFeature.realtimeAlerts} className={styles.tileChip} />}
            </span>
            <span className={styles.tileDescription}>{intl.formatMessage(messages[option.description])}</span>
          </label>
        );
      })}
    </fieldset>
  );
};

export const DigestTiles = TypedMemo(DigestTilesComponent);
